import React, { useEffect, useState } from "react";
import PageHeader from "../../component/pageHeaders";
import {
  Button,
  message,
  notification,
  Input,
  Select,
  Space,
  Card,
  Row,
  Col,
  Statistic,
  DatePicker,
  Tag,
  Alert,
  Dropdown,
} from "antd";
import type { MenuProps } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  BarChartOutlined,
  CloseOutlined,
  MoreOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import {
  SearchOutlined,
  ReloadOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";

import Loadingbox from "../../component/loadingbox";
import { API } from "../../config/API";
import { DELETE, GET, PUT } from "../../utils/apirequest";
import DataTable from "./components/datatable";
import ResetPasswordModal from "./components/resetPasswordModal";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface EpaperUser {
  id: number;
  full_name: string;
  email: string;
  phone: string;
  status: "active" | "inactive" | "suspended";
  email_verified: boolean;
  last_login: string | null;
  createdAt: string;
}

interface PaginationInfo {
  page: number;
  pageSize: number;
  total: number;
  pageCount: number;
}

interface StatsData {
  total: number;
  active: number;
  verified: number;
  newToday: number;
}

const EpaperUsers = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<EpaperUser[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    pageSize: 20,
    total: 0,
    pageCount: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [modeFilter, setModeFilter] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("");
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [stats, setStats] = useState<StatsData>({
    total: 0,
    active: 0,
    verified: 0,
    newToday: 0,
  });
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<EpaperUser | null>(null);
  const [agentsList, setAgentsList] = useState<{ value: string; label: string }[]>([]);
  const [agentFilter, setAgentFilter] = useState<string[]>([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentSearch, setAgentSearch] = useState("");
  const [agentZoneFilter, setAgentZoneFilter] = useState<string>("");
  const [agentDistrictFilter, setAgentDistrictFilter] = useState<string>("");
  const User = useSelector((state: any) => state?.User);
  const isAdmin = User?.user?.role?.id === 4 || User?.user?.role?.id === 6;

  const roleSuffix = (roleId?: number) => {
    if (roleId === 4) return "Admin";
    if (roleId === 5) return "Agent";
    if (roleId === 6) return "E-Paper Admin";
    return "";
  };

  // Fetch agents (and admins / epaper admins) for filter dropdown
  const fetchAgents = async (search: string = "") => {
    try {
      setAgentsLoading(true);
      const params = new URLSearchParams({
        page: "1",
        pageSize: "500",
        roles: "4,5,6",
        ...(search && { search }),
      });
      const res: any = await GET(`${API.EPAPER_AGENTS_LIST}?${params.toString()}`, null, null);
      if (res && res.data) {
        setAgentsList(
          res.data
            .filter((agent: any) => !!agent.code)
            .map((agent: any) => {
              const role = roleSuffix(agent.role?.id);
              const rolePart = role ? ` - ${role}` : "";
              return {
                value: agent.code,
                label: `${agent.username} (${agent.code})${rolePart}`,
              };
            })
        );
      }
    } catch (error) {
      console.error("Error fetching agents:", error);
    } finally {
      setAgentsLoading(false);
    }
  };

  // Debounce API search in Agent filter
  useEffect(() => {
    if (!isAdmin) return;
    const t = setTimeout(() => {
      fetchAgents(agentSearch);
    }, 400);
    return () => clearTimeout(t);
  }, [agentSearch]);

  console.log("User.user---->",User.user?.agent_code)

  // Fetch users data
  const fetchUsers = async (
    page: number = 1,
    pageSize: number = 20,
    search: string = "",
    status: string = "",
    mode: string = "",
    paymentStatus: string = "",
    dateRange?: [dayjs.Dayjs, dayjs.Dayjs] | null,
    agentCodes: string[] = [],
    agentZone: string = "",
    agentDistrict: string = ""
  ) => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        ...(search && { search }),
        ...(status && { status }),
        ...(mode && { mode }),
        ...(paymentStatus && { paymentStatus }),
        ...(agentCodes.length && { agentCodes: agentCodes.join(",") }),
        ...(!isAdmin && User?.user?.id
          ? { agentCodes: User.user.agent_code }
          : {}),
        ...(agentZone && { agentZone }),
        ...(agentDistrict && { agentDistrict }),
      });

      if (dateRange) {
        params.append("startDate", dateRange[0].startOf("day").toISOString());
        params.append("endDate", dateRange[1].endOf("day").toISOString());
      }

      const response = (await GET(
        `${API.EPAPER_USERS_LIST}?${params.toString()}`,
        null,
        null
      )) as any;

      if (response && response.data) {
        setData(response.data);
        setPagination(response.pagination);

        // Calculate stats - these stats might not be accurate with filtering
        const totalUsers = response.pagination.total;
        const activeUsers = response.data.filter(
          (user: EpaperUser) => user.status === "active"
        ).length;
        const verifiedUsers = response.data.filter(
          (user: EpaperUser) => user.email_verified
        ).length;
        const today = new Date().toDateString();
        const newTodayUsers = response.data.filter(
          (user: EpaperUser) =>
            new Date(user.createdAt).toDateString() === today
        ).length;

        setStats({
          total: totalUsers,
          active: activeUsers,
          verified: verifiedUsers,
          newToday: newTodayUsers,
        });
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      message.error("Failed to fetch users data");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle page change
  const handlePageChange = (page: number, pageSize: number) => {
    fetchUsers(
      page,
      pageSize,
      searchTerm,
      statusFilter,
      modeFilter,
      paymentStatusFilter,
      dateRange,
      agentFilter
    );
  };

  // Handle search
  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const applyFilters = () => {
    fetchUsers(
      1,
      pagination.pageSize,
      searchTerm,
      statusFilter,
      modeFilter,
      paymentStatusFilter,
      dateRange,
      agentFilter
    );
  };

  // Handle status filter change
  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
  };

  // Handle mode filter change
  const handleModeFilterChange = (value: string) => {
    setModeFilter(value);
  };

  // Handle payment status filter change
  const handlePaymentStatusFilterChange = (value: string) => {
    setPaymentStatusFilter(value);
  };

  // Handle date range change
  const handleDateRangeChange = (dates: any, dateStrings: [string, string]) => {
    if (dates) {
      setDateRange(dates);
    } else {
      setDateRange(null);
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    setSearchTerm("");
    setStatusFilter("");
    setModeFilter("");
    setPaymentStatusFilter("");
    setDateRange(null);
    setAgentFilter([]);
    setAgentZoneFilter("");
    setAgentDistrictFilter("");
    fetchUsers(1, pagination.pageSize);
  };

  // Handle user edit
  const handleEdit = (user: EpaperUser) => {
    // message.info(
    //   `Edit functionality for ${user.full_name} will be implemented`
    // );
    // TODO: Navigate to edit form or open edit modal
  };

  // Handle user delete
  const handleDelete = async (userId: number) => {
    try {
      await DELETE(API.EPAPER_USER_DELETE + `${userId}/delete`);
      message.success("EPaper user deleted successfully");
      fetchUsers(
        pagination.page,
        pagination.pageSize,
        searchTerm,
        statusFilter,
        modeFilter,
        paymentStatusFilter,
        dateRange,
        agentFilter
      );
    } catch (error) {
      console.error("Error deleting user:", error);
      message.error("Failed to delete user");
    }
  };

  // Handle toggle user status
  const handleToggleStatus = async (userId: number, newStatus: string) => {
    try {
      await PUT(API.EPAPER_USER_TOGGLE_STATUS + `${userId}/toggle-status`, {
        data: { status: newStatus },
      });
      notification.success({
        message: "Status Updated",
        description: `EPaper user status changed to ${newStatus}.`,
      });
      fetchUsers(
        pagination.page,
        pagination.pageSize,
        searchTerm,
        statusFilter,
        modeFilter,
        paymentStatusFilter,
        dateRange,
        agentFilter
      );
    } catch (error) {
      console.error("Error updating user status:", error);
      message.error("Failed to update user status");
    }
  };

  // Handle reset password
  const handleResetPassword = (user: EpaperUser) => {
    setSelectedUser(user);
    setResetPasswordModalOpen(true);
  };

  // Handle reset password submit callback
  const handleResetPasswordSubmit = () => {
    // Refresh the users list after password reset
    fetchUsers(
      pagination.page,
      pagination.pageSize,
      searchTerm,
      statusFilter,
      modeFilter,
      paymentStatusFilter,
      dateRange,
      agentFilter
    );
  };

  // Handle Excel export
  const handleExport = async () => {
    try {
      setExportLoading(true);
      const params = new URLSearchParams({
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter && { status: statusFilter }),
        ...(modeFilter && { mode: modeFilter }),
        ...(paymentStatusFilter && { paymentStatus: paymentStatusFilter }),
        ...(agentFilter.length && { agentCodes: agentFilter.join(",") }),
        ...(!isAdmin && User?.user?.id
          ? { agentCodes: User.user.agent_code }
          : {}),
      });

      if (dateRange) {
        params.append("startDate", dateRange[0].startOf("day").toISOString());
        params.append("endDate", dateRange[1].endOf("day").toISOString());
      }

      const response = (await GET(
        `${API.EPAPER_USERS_EXPORT}?${params.toString()}`,
        null,
        null
      )) as any;

      if (response && response.data) {
        const ws = XLSX.utils.json_to_sheet(response.data);
        // Bold the header row
        const range = XLSX.utils.decode_range(ws["!ref"] || "A1");
        for (let c = range.s.c; c <= range.e.c; c++) {
          const cell = XLSX.utils.encode_cell({ r: 0, c });
          if (ws[cell]) ws[cell].s = { font: { bold: true } };
        }
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "EPaper Users");

        const colWidths = [
          { wch: 6 },  // S.No
          { wch: 14 }, // Subscriber Code
          { wch: 22 }, // Full Name
          { wch: 28 }, // Email
          { wch: 15 }, // Phone
          { wch: 12 }, // Status
          { wch: 18 }, // Zone
          { wch: 14 }, // Register Mode
          { wch: 10 }, // Amount
          { wch: 12 }, // Payment Status
          { wch: 15 }, // Payment Mode
          { wch: 30 }, // Receipt Number
          { wch: 14 }, // Payment Date
          { wch: 22 }, // Agent Name
          { wch: 12 }, // Agent Code
          { wch: 18 }, // Subscription Start
          { wch: 18 }, // Subscription End
          { wch: 25 }, // Registered By
          { wch: 16 }, // Registration Date
          { wch: 14 }, // Last Login
        ];
        ws["!cols"] = colWidths;

        const filename =
          response.filename ||
          `epaper-users-${new Date().toISOString().split("T")[0]}`;

        XLSX.writeFile(wb, `${filename}.xlsx`);
        message.success(
          `Exported ${response.total} users to Excel successfully`
        );
      }
    } catch (error) {
      console.error("Error exporting users:", error);
      message.error("Failed to export users data");
    } finally {
      setExportLoading(false);
    }
  };

  // Initial load: read URL params from reports page navigation
  useEffect(() => {
    const zone = searchParams.get("agentZone") || "";
    const district = searchParams.get("agentDistrict") || "";
    const sd = searchParams.get("startDate");
    const ed = searchParams.get("endDate");
    setAgentZoneFilter(zone);
    setAgentDistrictFilter(district);
    const dr: [dayjs.Dayjs, dayjs.Dayjs] | null =
      sd && ed ? [dayjs(sd), dayjs(ed)] : null;
    if (dr) setDateRange(dr);
    fetchUsers(1, 20, "", "", "", "", dr, [], zone, district);
  }, []);

  return (
    <div className="p-4">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <PageHeader
          title="E-Paper Users Management"
          loader={0}
          length="Dashboard / E-Paper Users"
        />
        {isAdmin && (
          <Dropdown
            placement="bottomRight"
            trigger={["click"]}
            menu={{
              items: [
                {
                  key: "reports",
                  icon: <BarChartOutlined />,
                  label: "Reports",
                  onClick: () => navigate("/Auth/epaper-users/reports"),
                },
                {
                  key: "subscription-history",
                  icon: <HistoryOutlined />,
                  label: "Subscription History",
                  onClick: () =>
                    navigate("/Auth/epaper-users/subscriptions"),
                },
              ] as MenuProps["items"],
            }}
          >
            <Button
              type="default"
              icon={<MoreOutlined />}
              style={{ marginTop: 4 }}
            >
              More
            </Button>
          </Dropdown>
        )}
      </div>
      <br />
      {(agentZoneFilter || agentDistrictFilter) && (
        <Alert
          type="info"
          style={{ marginBottom: 12 }}
          message={
            <span>
              Filtered from Reports &mdash;{" "}
              {agentZoneFilter && <Tag color="blue">Zone: {agentZoneFilter}</Tag>}
              {agentDistrictFilter && <Tag color="green">District: {agentDistrictFilter}</Tag>}
              <Button
                type="link"
                size="small"
                icon={<CloseOutlined />}
                onClick={() => {
                  setAgentZoneFilter("");
                  setAgentDistrictFilter("");
                  fetchUsers(1, pagination.pageSize, searchTerm, statusFilter, modeFilter, paymentStatusFilter, dateRange, agentFilter);
                }}
              >
                Clear
              </Button>
            </span>
          }
        />
      )}
      {/* Stats Cards */}
      <Row gutter={16} className="mb-6">
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Users"
              value={stats.total}
              prefix={<UserOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        {/* <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Active Users"
              value={stats.active}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col> */}
        {/* <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Verified Users"
              value={stats.verified}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col> */}
        {/* <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="New Today"
              value={stats.newToday}
              prefix={<UserOutlined />}
              valueStyle={{ color: "#fa8c16" }}
            />
          </Card>
        </Col> */}
      </Row>
      <br />

      {/* Filters */}
      <Card className="mb-4">
        <Row gutter={[12, 12]} align="bottom">
          <Col xs={24} sm={12} md={isAdmin ? 5 : 7}>
            <p style={{ marginBottom: 4, fontWeight: 600, fontSize: 13 }}>Search</p>
            <Search
              placeholder="Name, email, phone, or subscriber code"
              allowClear
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onSearch={applyFilters}
              onPressEnter={applyFilters}
            />
          </Col>

          <Col xs={12} sm={8} md={isAdmin ? 3 : 4}>
            <p style={{ marginBottom: 4, fontWeight: 600, fontSize: 13 }}>Register Mode</p>
            <Select
              style={{ width: "100%" }}
              placeholder="All Modes"
              allowClear
              value={modeFilter || undefined}
              onChange={handleModeFilterChange}
            >
              <Option value="">All</Option>
              <Option value="paytabs">Paytabs</Option>
              <Option value="agent">Agent</Option>
            </Select>
          </Col>

          {isAdmin && (
            <Col xs={12} sm={8} md={4}>
              <p style={{ marginBottom: 4, fontWeight: 600, fontSize: 13 }}>Agent</p>
              <Select
                mode="multiple"
                style={{ width: "100%" }}
                placeholder="All Agents / Admins"
                allowClear
                loading={agentsLoading}
                maxTagCount="responsive"
                value={agentFilter}
                onChange={(values: string[]) => {
                  // If "all" was just selected, clear everything else
                  if (values.includes("all")) {
                    setAgentFilter([]);
                  } else {
                    setAgentFilter(values);
                  }
                }}
                options={[
                  { value: "all", label: "All" },
                  ...agentsList,
                ]}
                showSearch
                filterOption={false}
                onSearch={setAgentSearch}
                onBlur={() => setAgentSearch("")}
                notFoundContent={agentsLoading ? "Searching..." : "No match"}
              />
            </Col>
          )}

          <Col xs={12} sm={8} md={isAdmin ? 3 : 4}>
            <p style={{ marginBottom: 4, fontWeight: 600, fontSize: 13 }}>Payment Status</p>
            <Select
              style={{ width: "100%" }}
              placeholder="All"
              allowClear
              value={paymentStatusFilter || undefined}
              onChange={handlePaymentStatusFilterChange}
            >
              <Option value="">All</Option>
              <Option value="paid">Paid</Option>
              <Option value="free">Free</Option>
            </Select>
          </Col>

          <Col xs={24} sm={12} md={isAdmin ? 5 : 6}>
            <p style={{ marginBottom: 4, fontWeight: 600, fontSize: 13 }}>Registration Date</p>
            <RangePicker
              style={{ width: "100%" }}
              value={dateRange}
              onChange={handleDateRangeChange}
            />
          </Col>

          <Col xs={24} sm={24} md={isAdmin ? 4 : 3}
            style={{ display: "flex", gap: 8, alignItems: "flex-end" }}
          >
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={applyFilters}
              style={{ flex: 1 }}
            >
              Search
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              title="Reset filters"
            />
          </Col>
        </Row>
      </Card>

      {/* Data Table */}
      <Card>
        {isLoading ? (
          <Loadingbox />
        ) : (
          <DataTable
            data={data}
            loading={isLoading}
            page={pagination.page}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onPageChange={handlePageChange}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onToggleStatus={handleToggleStatus}
            onResetPassword={handleResetPassword}
            onExport={handleExport}
            exportLoading={exportLoading}
            reload={handleRefresh}
          />
        )}
      </Card>

      {/* Reset Password Modal */}
      <ResetPasswordModal
        open={resetPasswordModalOpen}
        onCancel={() => {
          setResetPasswordModalOpen(false);
          setSelectedUser(null);
        }}
        onSubmit={handleResetPasswordSubmit}
        user={selectedUser}
      />
    </div>
  );
};

export default EpaperUsers;
